import React, { useEffect, useState } from 'react';
import api from '../api/client';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useAuth } from '../context/AuthContext';
import { BookOpen, Shield, Users, Download, Loader2, Video, PlayCircle } from 'lucide-react';

const ICONS = {
  user:  BookOpen,
  admin: Shield,
  roles: Users,
};

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const Ayuda = () => {
  const { user } = useAuth();
  const [manuals, setManuals] = useState([]);
  const [active, setActive] = useState('user');
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [tutorials, setTutorials] = useState([]);
  const [playing, setPlaying] = useState(null); // id del vídeo en reproducción

  useEffect(() => {
    api.get('/docs/manuals').then((r) => {
      setManuals(r.data.items || []);
      if (user?.role === 'admin') setActive('admin');
      else setActive('user');
    });
    fetch(`${BACKEND}/api/tutorials`)
      .then((r) => r.json())
      .then((d) => setTutorials(d.tutorials || []))
      .catch(() => setTutorials([]));
  }, [user]);

  useEffect(() => {
    if (!active) return;
    setLoading(true);
    api.get(`/docs/manuals/${active}`)
      .then((r) => setMarkdown(r.data.markdown || ''))
      .finally(() => setLoading(false));
  }, [active]);

  const download = async (id) => {
    const r = await api.get(`/docs/manuals/${id}`);
    const blob = new Blob([r.data.markdown], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Adquiriam_${id}_manual.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-black text-white" data-testid="ayuda-page">
      <div className="max-w-6xl mx-auto p-4 sm:p-8">
        <header className="mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
          <div>
            <div className="text-xs font-bold tracking-widest text-[#A3FF12]">DOCUMENTACIÓN</div>
            <h1 className="text-3xl sm:text-4xl font-black text-white mt-1">Centro de ayuda</h1>
            <p className="text-white/50 text-sm mt-2 max-w-2xl">
              Manuales completos de Adquiriam. Paso a paso de cada apartado, con ventajas y posibilidades. Descárgalos en PDF o consúltalos aquí — se actualizan con cada versión.
            </p>
          </div>
          <a href={`${BACKEND}/api/docs/manuals/${active}/pdf`} target="_blank" rel="noreferrer"
            data-testid="manual-pdf-download"
            className="inline-flex items-center gap-2 bg-[#A3FF12] text-black font-black px-4 py-2.5 rounded-xl text-sm hover:bg-[#b8ff3a] flex-shrink-0">
            <Download size={16}/> Descargar {active === 'admin' ? 'Manual Admin' : active === 'roles' ? 'Roles' : 'Manual Usuario'} (PDF)
          </a>
        </header>

        <div className="flex flex-col lg:flex-row gap-6">
          {/* Sidebar */}
          <aside className="lg:w-64 flex-shrink-0">
            <div className="space-y-2 sticky top-4">
              {manuals.map((m) => {
                const Icon = ICONS[m.id] || BookOpen;
                const isActive = active === m.id;
                return (
                  <button
                    key={m.id}
                    onClick={() => setActive(m.id)}
                    data-testid={`manual-tab-${m.id}`}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl border transition-all text-left ${
                      isActive
                        ? 'bg-[#A3FF12]/10 border-[#A3FF12]/40 text-[#A3FF12]'
                        : 'bg-black/30 border-white/10 text-white/70 hover:bg-white/5 hover:border-white/20'
                    }`}
                  >
                    <Icon size={18} className="flex-shrink-0"/>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-bold truncate">{m.label}</div>
                      <div className="text-[10px] text-white/40">{(m.size_bytes / 1024).toFixed(1)} KB</div>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); download(m.id); }}
                      className="text-white/40 hover:text-[#A3FF12] p-1"
                      title="Descargar Markdown"
                      data-testid={`manual-download-${m.id}`}
                    >
                      <Download size={14}/>
                    </button>
                  </button>
                );
              })}
            </div>
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0">
            <article className="prose prose-invert prose-sm sm:prose-base max-w-none
              prose-headings:text-white
              prose-h1:text-3xl prose-h1:font-black prose-h1:text-[#A3FF12] prose-h1:border-b prose-h1:border-white/10 prose-h1:pb-3
              prose-h2:text-xl prose-h2:font-black prose-h2:text-white prose-h2:mt-8
              prose-h3:text-base prose-h3:font-bold prose-h3:text-white/90 prose-h3:mt-4
              prose-a:text-[#A3FF12] prose-a:no-underline hover:prose-a:underline
              prose-strong:text-white
              prose-code:text-[#A3FF12] prose-code:bg-white/5 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-xs prose-code:before:content-none prose-code:after:content-none
              prose-hr:border-white/10
              prose-table:text-sm prose-th:text-[#A3FF12] prose-th:border-white/20 prose-td:border-white/10 prose-td:text-white/80
              prose-li:text-white/80 prose-ul:text-white/80 prose-ol:text-white/80
              prose-p:text-white/80
              prose-blockquote:border-l-[#A3FF12]/50 prose-blockquote:text-white/70
              bg-[#0a0a0a] border border-white/10 rounded-2xl p-5 sm:p-8"
              data-testid="manual-content"
            >
              {loading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="animate-spin text-[#A3FF12]" size={32}/>
                </div>
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{markdown}</ReactMarkdown>
              )}
            </article>
          </main>
        </div>

        {/* ─── Vídeos tutoriales ─── */}
        <section className="mt-10 border-t border-white/10 pt-8" data-testid="tutorial-videos-section">
          <div className="flex items-center gap-2 mb-1">
            <Video className="text-[#A3FF12]" size={20}/>
            <h2 className="text-xl sm:text-2xl font-black text-white">Vídeos tutoriales</h2>
          </div>
          <p className="text-white/50 text-sm mb-4">
            Tutoriales narrados en español, capturados de la app en vivo. Pulsa cualquier vídeo para verlo aquí mismo.
          </p>
          {tutorials.length === 0 ? (
            <div className="p-8 text-center text-white/40 text-sm border border-white/10 rounded-xl bg-white/5">
              Los vídeos aún se están generando. Refresca en unos minutos.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {tutorials.map((t) => {
                const ready = t.size_bytes > 0;
                const isPlaying = playing === t.id;
                return (
                  <div key={t.id}
                    className={`rounded-xl overflow-hidden border ${ready ? 'border-white/10 hover:border-[#A3FF12]/40 bg-black' : 'border-white/5 bg-white/[0.02] opacity-60'}`}
                    data-testid={`tutorial-${t.id}`}>
                    {isPlaying ? (
                      <video src={`${BACKEND}/api/tutorials/${t.id}.mp4`} controls autoPlay
                        className="w-full aspect-video bg-black"
                        onEnded={() => setPlaying(null)}/>
                    ) : (
                      <button onClick={() => ready && setPlaying(t.id)} disabled={!ready}
                        data-testid={`play-${t.id}`}
                        className="w-full aspect-video bg-gradient-to-br from-[#111] to-black flex items-center justify-center group disabled:cursor-not-allowed">
                        {ready
                          ? <PlayCircle className="text-[#A3FF12] group-hover:scale-110 transition-transform" size={54}/>
                          : <Loader2 className="text-white/30 animate-spin" size={28}/>}
                      </button>
                    )}
                    <div className="p-3">
                      <div className="text-white font-black text-sm">{t.title}</div>
                      <div className="text-white/40 text-xs mt-1 flex items-center justify-between">
                        <span>{t.scenes} escenas</span>
                        {ready && (
                          <a href={`${BACKEND}/api/tutorials/${t.id}.mp4`} download
                            data-testid={`download-${t.id}`}
                            className="text-[#A3FF12] hover:underline flex items-center gap-1">
                            <Download size={11}/> {(t.size_bytes / 1024).toFixed(0)} KB
                          </a>
                        )}
                        {!ready && <span className="italic">Generando…</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default Ayuda;
